/**
 * Shared Axios instance for admin API calls that require authentication.
 *
 * Behaviour:
 *  - Attaches the stored access token as Authorization header on every request.
 *  - On the first 401 response for a request, silently calls POST /auth/refresh
 *    using the stored refresh token, stores the new tokens, and retries the
 *    original request exactly once.
 *  - If refresh itself fails (no refresh token, expired, network error), clears
 *    all stored tokens and forces a page reload so react-admin redirects to login.
 */

import axios from "axios";
import { AppSettings } from "../config/appSettings";

const BASE_URL = `${AppSettings.server.baseUrl}/api/v1`;

const axiosWithAuth = axios.create({ baseURL: BASE_URL });

function setAuthHeader(headers, token) {
  const value = `Bearer ${token}`;

  if (headers instanceof Headers) {
    headers.set("Authorization", value);
    return headers;
  }

  if (headers && typeof headers.set === "function") {
    if (typeof headers.delete === "function") {
      headers.delete("Authorization");
      headers.delete("authorization");
    }
    headers.set("Authorization", value);
    return headers;
  }

  const nextHeaders = headers || {};
  delete nextHeaders.Authorization;
  delete nextHeaders.authorization;
  nextHeaders.authorization = value;
  return nextHeaders;
}

// ── Request interceptor: always attach current access token ──────────────────
axiosWithAuth.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = setAuthHeader(config.headers, token);
  }
  return config;
});

// ── Response interceptor: silent token refresh on first 401 ─────────────────
let isRefreshing = false;
// Queue of { resolve, reject } for requests that arrived while a refresh was
// already in flight. They will all be retried once the new token is available.
let pendingQueue = [];

function processQueue(error, newToken = null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newToken);
    }
  });
  pendingQueue = [];
}

async function forceLogout() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  // Hard reload so react-admin's authProvider.checkAuth() triggers the login
  // redirect without needing access to the react-admin store here.
  window.location.reload();
}

axiosWithAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const is401 = error.response?.status === 401;
    const requestUrl = originalRequest?.url || "";
    const isRefreshRequest = requestUrl.includes("/auth/refresh");

    // Some backend paths return a plain 401 instead of the TOKEN_EXPIRED
    // payload. Retry any auth failure once via refresh, except the refresh
    // request itself.
    if (!is401 || isRefreshRequest || originalRequest._retried) {
      return Promise.reject(error);
    }

    originalRequest._retried = true;

    if (isRefreshing) {
      // Another request is already refreshing — queue this one.
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers = setAuthHeader(
            originalRequest.headers,
            newToken,
          );
          return axiosWithAuth(originalRequest);
        })
        .catch(Promise.reject.bind(Promise));
    }

    isRefreshing = true;

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      isRefreshing = false;
      await forceLogout();
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
        refreshToken,
      });

      const newAccessToken = data.accessToken || data.token;
      const newRefreshToken = data.refreshToken;

      localStorage.setItem("token", newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      // Unblock any queued requests with the fresh token.
      processQueue(null, newAccessToken);

      // Retry the original request with the new token.
      originalRequest.headers = setAuthHeader(
        originalRequest.headers,
        newAccessToken,
      );
      return axiosWithAuth(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      await forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axiosWithAuth;
